import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconLinkedScope } from "../../../assets/svg/linkedscope-icon.svg";
import { ReactComponent as LogoClaude } from "../../../assets/svg/logo-claude.svg";
import { ReactComponent as LogoGemini } from "../../../assets/svg/logo-gemini.svg";
import { ReactComponent as LogoOpenai } from "../../../assets/svg/logo-openai.svg";
import linkedinLogo from "../../../assets/svg/logo-linkedin.svg";
import googleAdsLogo from "../../../assets/svg/logo-google-ads.svg";
import metaLogo from "../../../assets/svg/logo-meta.svg";
import {
  SHOWCASE_HOLD_MS,
  SHOWCASE_ITEM_DELAY_MS,
  SHOWCASE_WORD_DELAY_MS,
  buildNarrativeSteps,
  buildShowcaseTimeline,
  finalShowcaseState,
  pickNarrativeIndex,
} from "../../../utils/loginShowcaseChat";

// Narratives rotate at random on each page load; their copy (subtitle and rounds)
// lives under auth.login.showcase.chat.narratives.<key> and the MCP tools each round calls are here.
const NARRATIVES = [
  {
    key: "diagnosis",
    tools: [
      ["get_paid_media_diagnostic_summary", "get_paid_media_summary"],
      ["add_company_to_exclusions", "add_google_negative_keywords"],
    ],
  },
  {
    key: "linkedinCampaign",
    tools: [["get_account_context", "resolve_linkedin_targeting_entities", "create_campaign"]],
  },
  {
    key: "pipeline",
    tools: [
      ["get_pipeline_summary", "get_paid_media_summary"],
      ["pause_campaigns", "update_paid_media_campaigns"],
    ],
  },
  {
    key: "googleCampaign",
    tools: [["get_google_ads_keyword_plan", "create_campaign"]],
  },
];
const NARRATIVE_STORAGE_KEY = "linkedscope_login_showcase_narrative";
// Heights even out the optical size of the three wordmarks.
const PLATFORMS = {
  linkedin: { name: "LinkedIn Ads", logo: linkedinLogo, height: 16 },
  google: { name: "Google Ads", logo: googleAdsLogo, height: 18 },
  meta: { name: "Meta Ads", logo: metaLogo, height: 13 },
};
const ASSISTANTS = [
  { name: "Claude", Icon: LogoClaude },
  { name: "ChatGPT", Icon: LogoOpenai },
  { name: "Gemini", Icon: LogoGemini },
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/** Draws this load's narrative, remembering it so the next refresh shows another one. */
function chooseNarrative() {
  let previous = null;
  try {
    const stored = Number.parseInt(window.sessionStorage.getItem(NARRATIVE_STORAGE_KEY), 10);
    previous = Number.isInteger(stored) ? stored : null;
  } catch {
    previous = null;
  }
  const index = pickNarrativeIndex(NARRATIVES.length, previous);
  try {
    window.sessionStorage.setItem(NARRATIVE_STORAGE_KEY, String(index));
  } catch {
    // Storage can be unavailable (private mode); the narrative is still random.
  }
  return NARRATIVES[index];
}

/** Plays the conversation in a loop; with reduced motion it shows the final state once. */
function useShowcasePlayback(timeline) {
  const [reducedMotion] = useState(prefersReducedMotion);
  const [visible, setVisible] = useState(() => (reducedMotion ? finalShowcaseState(timeline.events) : new Set()));
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (reducedMotion) {
      setVisible(finalShowcaseState(timeline.events));
      return undefined;
    }
    setVisible(new Set());
    const timers = timeline.events.map(({ at, id, hide }) => setTimeout(() => {
      setVisible((current) => {
        const next = new Set(current);
        if (hide) next.delete(id);
        else next.add(id);
        return next;
      });
    }, at));
    timers.push(setTimeout(() => setCycle((value) => value + 1), timeline.duration + SHOWCASE_HOLD_MS));
    return () => timers.forEach(clearTimeout);
  }, [cycle, reducedMotion, timeline]);

  return { visible, reducedMotion };
}

function StreamedText({ text, lead = "" }) {
  let index = 0;
  const words = (value) => String(value).split(/(\s+)/).filter(Boolean).map((part) => {
    if (/^\s+$/.test(part)) return part;
    const delay = index * SHOWCASE_WORD_DELAY_MS;
    index += 1;
    return <span key={index} className="login-showcase-word" style={{ animationDelay: `${delay}ms` }}>{part}</span>;
  });
  return (
    <>
      {lead && <strong>{words(lead)}</strong>}
      {lead && " "}
      {words(text)}
    </>
  );
}

function Typing({ className }) {
  return <div className={`login-showcase-typing ${className}`}><span><i /><i /><i /></span></div>;
}

function Round({ round, index, tools, labels, stepClass, isVisible }) {
  const id = (name) => `r${index}-${name}`;
  const findings = asArray(round.findings);
  const previewItems = asArray(round.preview?.items);
  const isConfirmed = isVisible(id("confirm"));

  return (
    <>
      <p className={`login-showcase-question ${stepClass(id("question"))}`}>{round.question}</p>

      <div className={`login-showcase-reply ${stepClass(tools.length ? id("tool-0") : id("typing"))}`}>
        <span className="login-showcase-avatar"><IconLinkedScope /></span>
        <div className="login-showcase-reply-body">
          {tools.length > 0 && (
            <div className="login-showcase-tools">
              {tools.map((tool, toolIndex) => (
                <span key={tool} className={`login-showcase-tool ${stepClass(id(`tool-${toolIndex}`))}`}>
                  linkedscope.<b>{tool}</b>
                </span>
              ))}
            </div>
          )}

          {round.answer && (
            <>
              <Typing className={stepClass(id("typing"))} />
              <p className={`login-showcase-answer ${stepClass(id("answer"))}`}><StreamedText text={round.answer} /></p>
            </>
          )}

          {findings.length > 0 && (
            <div className={`login-showcase-findings ${stepClass(id("findings"))}`}>
              {findings.map((finding, findingIndex) => {
                const platform = PLATFORMS[finding.platform];
                return (
                  <div key={finding.text} className="login-showcase-finding" style={{ animationDelay: `${findingIndex * SHOWCASE_ITEM_DELAY_MS}ms` }}>
                    {platform && <img src={platform.logo} alt={platform.name} style={{ height: platform.height }} />}
                    <p>{finding.text}</p>
                  </div>
                );
              })}
            </div>
          )}

          {round.preview && (
            <div className={`login-showcase-preview ${stepClass(id("preview"))}`}>
              <div className="login-showcase-preview-head">
                <span>{round.preview.title}</span>
                <em>{round.preview.account}</em>
              </div>
              <ul>
                {previewItems.map((item, itemIndex) => (
                  <li key={item.label} style={{ animationDelay: `${itemIndex * SHOWCASE_ITEM_DELAY_MS}ms` }}>
                    <span>{itemIndex + 1}</span>
                    <p><strong>{item.label}</strong> {item.text}</p>
                  </li>
                ))}
                <li style={{ animationDelay: `${previewItems.length * SHOWCASE_ITEM_DELAY_MS}ms` }}>
                  <span>i</span>
                  <p>{round.preview.note}</p>
                </li>
              </ul>
              <div className="login-showcase-preview-actions">
                <span className={`login-showcase-confirm ${isConfirmed ? "is-confirmed" : ""}`}>
                  {isConfirmed ? `${labels.confirmed} ✓` : labels.confirm}
                </span>
                <span className="login-showcase-cancel">{labels.cancel}</span>
              </div>
            </div>
          )}

          {round.done && (
            <>
              <Typing className={stepClass(id("done-typing"))} />
              <p className={`login-showcase-answer ${stepClass(id("done"))}`}>
                <StreamedText lead={round.doneLead} text={round.done} />
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Right half of the login/signup pages: introduces the LinkedScope MCP, the entry
 * point people reach when they connect an AI assistant, with a simulated
 * conversation (diagnosis, campaign creation or pipeline) like the MCP launch post.
 */
function LoginShowcase() {
  const { t } = useTranslation("commonTranslation");
  const chat = (key, options) => t(`auth.login.showcase.chat.${key}`, options);
  const [narrative] = useState(chooseNarrative);
  const rounds = asArray(chat(`narratives.${narrative.key}.rounds`, { returnObjects: true }));
  const roundsSignature = JSON.stringify(rounds);

  const timeline = useMemo(
    () => buildShowcaseTimeline(buildNarrativeSteps(JSON.parse(roundsSignature), narrative.tools)),
    [roundsSignature, narrative]
  );
  const { visible, reducedMotion } = useShowcasePlayback(timeline);
  const isVisible = (id) => visible.has(id);
  const stepClass = (id) => (visible.has(id) ? "is-visible" : "");
  const feedRef = useRef(null);
  const labels = { confirm: chat("confirm"), confirmed: chat("confirmed"), cancel: chat("cancel") };

  // Keeps the latest message in view, like a real chat.
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed || typeof feed.scrollTo !== "function") return;
    const top = visible.size === 0 ? 0 : feed.scrollHeight;
    feed.scrollTo({ top, behavior: reducedMotion || visible.size === 0 ? "auto" : "smooth" });
  }, [visible, reducedMotion]);

  return (
    <aside className="login-showcase" data-narrative={narrative.key}>
      <div className="login-showcase-content">
        <span className="login-showcase-chip">{t("auth.login.showcase.eyebrow")}</span>
        <h2 className="login-showcase-title">
          {t("auth.login.showcase.titleStart")}{" "}
          <span className="login-showcase-highlight">{t("auth.login.showcase.titleHighlight")}</span>
        </h2>
        <p className="login-showcase-description">{chat(`narratives.${narrative.key}.description`)}</p>

        <div className="login-showcase-chat" aria-hidden="true">
          <div className="login-showcase-chat-bar">
            <span className="login-showcase-chat-dots"><i /><i /><i /></span>
            <span className="login-showcase-chat-title">{chat("windowTitle")}</span>
            <span className="login-showcase-chat-live">{chat("connected")}</span>
          </div>

          <div ref={feedRef} className={`login-showcase-feed ${reducedMotion ? "is-static" : ""}`}>
            {rounds.map((round, index) => (
              <Round
                key={round.question}
                round={round}
                index={index}
                tools={asArray(narrative.tools[index])}
                labels={labels}
                stepClass={stepClass}
                isVisible={isVisible}
              />
            ))}
          </div>

          <div className="login-showcase-input">
            <span>{chat("inputPlaceholder")}</span>
            <b>➜</b>
          </div>
        </div>

        <div className="login-showcase-assistants">
          <span>{t("auth.login.showcase.worksWith")}</span>
          <ul>
            {ASSISTANTS.map(({ name, Icon }) => (
              <li key={name}>
                <Icon aria-hidden="true" />
                {name}
              </li>
            ))}
            <li className="login-showcase-more">{t("auth.login.showcase.andMore")}</li>
          </ul>
        </div>
      </div>
    </aside>
  );
}

export default LoginShowcase;
